import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, Users, FileText, TrendingUp, Clock, 
  CheckCircle, AlertTriangle, BarChart3, PieChart,
  MapPin, GraduationCap, Building2, User
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Footer from '@/components/Footer';

const AdminMetricsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Acesso negado');
      navigate('/dashboard');
      return;
    }
    fetchMetrics();
  }, [user]);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/metrics');
      const data = response.data;
      
      // Transform API response to expected format
      const transformedMetrics = {
        totals: {
          users: data.total_users || 0,
          calls: data.total_calls || 0,
          active_calls: data.active_calls || 0,
          suggestions: data.total_suggestions || 0
        },
        users_by_role: data.users_by_role || { researchers: 0, editors: 0, verified_editors: 0, admins: 0 },
        pending: {
          suggestions: data.pending?.suggestions || 0,
          verifications: data.pending?.verifications || 0,
          reminders: 0
        },
        calls_timeline: {
          today: data.calls_stats?.today || 0,
          this_week: data.calls_stats?.this_week || 0,
          this_month: data.calls_stats?.this_month || 0
        },
        users_timeline: {
          today: 0,
          this_week: data.users_stats?.this_week || 0,
          this_month: data.users_stats?.this_month || 0
        },
        calls_by_qualis: data.qualis_distribution || [],
        calls_by_area: data.areas_distribution || [],
        demographics: {
          by_gender: data.demographics?.gender || [],
          by_age_range: data.demographics?.age || [],
          by_titulacao: data.demographics?.titulacao || [],
          by_institution: data.demographics?.institutions || [],
          by_state: data.demographics?.states || [],
          by_city: []
        }
      };
      
      setMetrics(transformedMetrics);
    } catch (error) {
      toast.error('Erro ao carregar métricas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#002FA7] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Carregando métricas...</p>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex flex-col">
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Dashboard de Métricas</h1>
          </div>
          <Button variant="outline" onClick={fetchMetrics}>
            Atualizar
          </Button>
        </div>
      </header>

      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 w-full">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-500">Total Usuários</span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold">{metrics.totals.users}</p>
          </div>
          
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-gray-500">Chamadas Ativas</span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold">{metrics.totals.active_calls}</p>
          </div>
          
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="h-5 w-5 text-yellow-600" />
              <span className="text-sm font-medium text-gray-500">Pendentes</span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold">{metrics.pending.suggestions + metrics.pending.verifications}</p>
          </div>
          
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="h-5 w-5 text-purple-600" />
              <span className="text-sm font-medium text-gray-500">Total Chamadas</span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold">{metrics.totals.calls}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Users by Role */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Users className="h-5 w-5" />
              Usuários por Tipo
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <span>Pesquisadores</span>
                <Badge variant="outline">{metrics.users_by_role.researchers}</Badge>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <span>Editores</span>
                <Badge variant="outline">{metrics.users_by_role.editors}</Badge>
              </div>
              <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                <span className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Editores Verificados
                </span>
                <Badge className="bg-green-100 text-green-700">{metrics.users_by_role.verified_editors}</Badge>
              </div>
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                <span>Administradores</span>
                <Badge className="bg-blue-100 text-blue-700">{metrics.users_by_role.admins}</Badge>
              </div>
            </div>
          </div>

          {/* Pending Actions */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Ações Pendentes
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-yellow-50 rounded">
                <span>Sugestões de Chamadas</span>
                <Badge className="bg-yellow-100 text-yellow-700">{metrics.pending.suggestions}</Badge>
              </div>
              <div className="flex justify-between items-center p-3 bg-yellow-50 rounded">
                <span>Verificações de Editores</span>
                <Badge className="bg-yellow-100 text-yellow-700">{metrics.pending.verifications}</Badge>
              </div>
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                <span>Lembretes Programados</span>
                <Badge className="bg-blue-100 text-blue-700">{metrics.pending.reminders}</Badge>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Timeline - Calls */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-green-600" />
              Chamadas Cadastradas
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                <span>Hoje</span>
                <Badge className="bg-green-100 text-green-700">{metrics.calls_timeline.today}</Badge>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <span>Últimos 7 dias</span>
                <Badge variant="outline">{metrics.calls_timeline.this_week}</Badge>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <span>Últimos 30 dias</span>
                <Badge variant="outline">{metrics.calls_timeline.this_month}</Badge>
              </div>
            </div>
          </div>

          {/* Timeline - Users */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Novos Usuários
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                <span>Hoje</span>
                <Badge className="bg-blue-100 text-blue-700">{metrics.users_timeline.today}</Badge>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <span>Últimos 7 dias</span>
                <Badge variant="outline">{metrics.users_timeline.this_week}</Badge>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <span>Últimos 30 dias</span>
                <Badge variant="outline">{metrics.users_timeline.this_month}</Badge>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Calls by Qualis */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <PieChart className="h-5 w-5 text-purple-600" />
              Chamadas por QUALIS
            </h3>
            <div className="space-y-2">
              {metrics.calls_by_qualis.map(item => (
                <div key={item._id} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded">
                  <span className="flex items-center gap-2">
                    <Badge className={`qualis-${item._id?.toLowerCase() || 'unknown'} font-semibold`}>
                      {item._id || 'N/A'}
                    </Badge>
                  </span>
                  <span className="font-semibold">{item.count}</span>
                </div>
              ))}
              {metrics.calls_by_qualis.length === 0 && (
                <p className="text-gray-500 text-center py-4">Nenhuma chamada cadastrada</p>
              )}
            </div>
          </div>

          {/* Calls by Area */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <PieChart className="h-5 w-5 text-orange-600" />
              Áreas Mais Populares
            </h3>
            <div className="space-y-2">
              {metrics.calls_by_area.map((item, index) => (
                <div key={item._id} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded">
                  <span className="text-sm truncate max-w-[250px]" title={item._id}>
                    {index + 1}. {item._id}
                  </span>
                  <Badge variant="outline">{item.count}</Badge>
                </div>
              ))}
              {metrics.calls_by_area.length === 0 && (
                <p className="text-gray-500 text-center py-4">Nenhuma chamada cadastrada</p>
              )}
            </div>
          </div>
        </div>

        {/* Demographics Section */}
        <h2 className="text-xl font-bold mt-10 mb-6 flex items-center gap-2">
          <Users className="h-6 w-6 text-[#002FA7]" />
          Dados Demográficos dos Usuários
        </h2>

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Users by Gender */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <User className="h-5 w-5 text-pink-600" />
              Por Gênero
            </h3>
            <div className="space-y-2">
              {metrics.demographics?.by_gender?.map(item => (
                <div key={item._id} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded">
                  <span>{item._id || 'Não informado'}</span>
                  <Badge variant="outline">{item.count}</Badge>
                </div>
              ))}
              {(!metrics.demographics?.by_gender || metrics.demographics.by_gender.length === 0) && (
                <p className="text-gray-500 text-center py-4">Sem dados</p>
              )}
            </div>
          </div>

          {/* Users by Age Range */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Por Faixa Etária
            </h3>
            <div className="space-y-2">
              {metrics.demographics?.by_age_range?.map(item => (
                <div key={item._id} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded">
                  <span>{item._id}</span>
                  <Badge variant="outline">{item.count}</Badge>
                </div>
              ))}
              {(!metrics.demographics?.by_age_range || metrics.demographics.by_age_range.length === 0) && (
                <p className="text-gray-500 text-center py-4">Sem dados</p>
              )}
            </div>
          </div>

          {/* Users by Titulação */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-purple-600" />
              Por Titulação
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {metrics.demographics?.by_titulacao?.map(item => (
                <div key={item._id} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded">
                  <span className="text-sm truncate max-w-[180px]" title={item._id}>{item._id}</span>
                  <Badge variant="outline">{item.count}</Badge>
                </div>
              ))}
              {(!metrics.demographics?.by_titulacao || metrics.demographics.by_titulacao.length === 0) && (
                <p className="text-gray-500 text-center py-4">Sem dados</p>
              )}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Users by Institution */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-indigo-600" />
              Por Instituição de Ensino
            </h3>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {metrics.demographics?.by_institution?.map((item, index) => (
                <div key={item._id} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded">
                  <span className="text-sm truncate max-w-[280px]" title={item._id}>
                    {index + 1}. {item._id}
                  </span>
                  <Badge variant="outline">{item.count}</Badge>
                </div>
              ))}
              {(!metrics.demographics?.by_institution || metrics.demographics.by_institution.length === 0) && (
                <p className="text-gray-500 text-center py-4">Sem dados</p>
              )}
            </div>
          </div>

          {/* Users by Location */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-red-600" />
              Por Localização
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {/* By State */}
              <div>
                <h4 className="font-semibold text-sm text-gray-600 mb-2">Estados</h4>
                <div className="space-y-1">
                  {metrics.demographics?.by_state?.slice(0, 8).map(item => (
                    <div key={item._id} className="flex justify-between items-center text-sm p-1.5 hover:bg-gray-50 rounded">
                      <span>{item._id}</span>
                      <span className="text-gray-600">{item.count}</span>
                    </div>
                  ))}
                  {(!metrics.demographics?.by_state || metrics.demographics.by_state.length === 0) && (
                    <p className="text-gray-400 text-sm">Sem dados</p>
                  )}
                </div>
              </div>
              {/* By City */}
              <div>
                <h4 className="font-semibold text-sm text-gray-600 mb-2">Cidades</h4>
                <div className="space-y-1">
                  {metrics.demographics?.by_city?.slice(0, 8).map(item => (
                    <div key={item._id} className="flex justify-between items-center text-sm p-1.5 hover:bg-gray-50 rounded">
                      <span className="truncate max-w-[100px]" title={item._id}>{item._id}</span>
                      <span className="text-gray-600">{item.count}</span>
                    </div>
                  ))}
                  {(!metrics.demographics?.by_city || metrics.demographics.by_city.length === 0) && (
                    <p className="text-gray-400 text-sm">Sem dados</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminMetricsPage;
